import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const volumes = [{
  icon: '📗',
  number: 'Volume 1 — Food Science Foundations',
  title: 'Food Science Foundations',
  subtitle: 'The foundation students need to understand why Food Science matters from day one',
  problem: 'Starting a Food Science course is one of the most exciting — and demanding — moments of the school year. Students arrive with no framework for what Food Science actually is or why it matters. This volume builds that foundation clearly, logically, and engagingly.',
  bullets: ['Introduce Food Science as a discipline with real-world relevance', 'Explain physical and chemical properties of food in accessible language', 'Teach water, carbohydrates, proteins, and fats with clear structure', 'Guide students through heat changes, texture, color, flavor, and aroma', 'Use ready-to-go measurement, observation, and ingredient analysis activities'],
  result: 'Your students build a real scientific foundation for food — and you start the year with structure, clarity, and zero scrambling.',
  popular: true
}, {
  icon: '📘',
  number: 'Volume 2 — Food Safety and Sanitation',
  title: 'Food Safety and Sanitation',
  subtitle: 'Critical safety concepts made engaging, clear, and ready to teach',
  problem: 'Food Safety is the unit every Foods teacher knows is critical — and the one that is hardest to keep engaging week after week. This volume gives you 24 fully structured lessons with real scenarios and clear explanations that make students take the content seriously.',
  bullets: ['Explain biological, chemical, and physical hazards with real examples', 'Cover foodborne illness, contamination, and cross-contamination in depth', 'Deliver personal hygiene, handwashing, and sanitation lessons step by step', 'Teach temperature danger zone, safe storage, and handling protocols', 'Use real food safety situations for analysis and discussion'],
  result: 'Your students leave this unit understanding food safety at a level that stays with them — and you teach every lesson with complete confidence.'
}, {
  icon: '📙',
  number: 'Volume 3 — Food Processing and Preservation',
  title: 'Food Processing and Preservation',
  subtitle: 'Technical food science made accurate, engaging, and classroom-ready',
  problem: 'Processing and preservation is the unit where Food Science gets technical — and where most teachers struggle to find materials that are accurate, engaging, and built for a high school classroom. This volume eliminates that problem entirely.',
  bullets: ['Explain raw materials, ingredient selection, and quality clearly', 'Teach refrigeration, freezing, drying, pasteurization, canning, and fermentation', 'Make salt, sugar, and acid preservation chemistry understandable', 'Bring packaging, shelf life, and storage knowledge into the classroom', 'Compare physical and chemical changes during processing'],
  result: 'Your students understand how food is transformed and preserved at an industry level — and you teach this demanding unit without building a single resource from scratch.'
}, {
  icon: '📕',
  number: 'Volume 4 — Quality, Industry and Product Development',
  title: 'Quality, Industry and Product Development',
  subtitle: 'The unit that turns Food Science into a real career pathway',
  problem: 'This is the unit that shows students what Food Science looks like as a real career — and it is hard to teach well without actual industry knowledge built into the materials. This volume brings quality control, sustainability, and a complete food innovation project together.',
  bullets: ['Structure quality control, assurance, sampling, and defect identification', 'Cover traceability, labeling, Nutrition Facts, and date markings', 'Connect production planning, costing, and waste reduction to Food Science', 'Include product development, evaluation, and sensory analysis', 'Assign a fully designed final food innovation project'],
  result: 'Your students finish the course with a genuine understanding of the food industry — and you close the year with a professional final project, not a rushed one.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-251" data-mp="9igbuyr7nta">
      <div className="max-w-6xl mx-auto" data-id="element-252" data-mp="87cxw980i2p">
        <div className="text-center mb-16" data-id="element-253" data-mp="75tb3e52n9d">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-254" data-mp="519zvljhst9x">
            📚 90 Ready-to-Teach Food Science Lessons
          </h2>
          <p className="text-xl text-gold-light" data-id="element-255" data-mp="93wx61yxn6ei">
            4 Volumes · Full Year Coverage · Nothing to Build From Scratch
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-256" data-mp="j11jq2wox3vu">
          {volumes.map((volume, index) => <motion.article key={volume.number} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-dark-card rounded-2xl border ${volume.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-dark-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-257" data-mp="t4ydr4hefcgc" data-mpi={index}>
              {volume.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-258" data-mp="16xekogdf7umj">
                  START HERE
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-259" data-mp="y0x8rax8fspq">
                <span className="text-4xl" aria-hidden="true" data-id="element-260" data-mp="1ulxrergtnzci">{volume.icon}</span>
                <div data-id="element-261" data-mp="1ptxnknxmsman">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-262" data-mp="1pte7lcrjk7z9">
                    {volume.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-263" data-mp="3p14m6yniirmz">
                    {volume.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-264" data-mp="3aadwpx9e84n1">
                {volume.subtitle}
              </p>

              <div className="mb-6" data-id="element-265" data-mp="31aig3cx2jhfm">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-266" data-mp="31a8r9snrfgbi">
                  What does this solve in your teaching?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-267" data-mp="2rens02a2su5g">{volume.problem}</p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-268" data-mp="2wcz1ud0df4no">
                {volume.bullets.map(bullet => <li key={bullet} className="flex items-start gap-3" data-id="element-269" data-mp="pmsjfvj495rg">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-270" data-mp="uk15i77dxtxz" />
                    <span className="text-gray-200 text-sm" data-id="element-271" data-mp="2f2d1dudw4jtn">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-272" data-mp="2a5mmvbhqhwac">
                <span className="text-gold font-bold block mb-1" data-id="element-273" data-mp="2otpsgjrv75ym">Result:</span>
                <p className="text-white text-sm" data-id="element-274" data-mp="2k1xxzy5l8g37">{volume.result}</p>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}